/**
 * GCS resumable upload protocol (storage-and-uploads.md §5): the browser PUTs slices straight to
 * the session URI forint issued. Nothing here talks to forint; progress reports live in useUpload.
 */

export class UploadHttpError extends Error {
  constructor(public status: number) {
    super(`upload failed with HTTP ${status}`);
  }
}

/** Asks GCS how much it has. 308 with a Range header → next byte; no Range → 0; 200/201 → done. */
export async function queryOffset(sessionUri: string, total: number, signal?: AbortSignal): Promise<number | 'done'> {
  const r = await fetch(sessionUri, { method: 'PUT', headers: { 'Content-Range': `bytes */${total}` }, signal });
  if (r.status === 200 || r.status === 201) return 'done';
  if (r.status !== 308) throw new UploadHttpError(r.status);
  const range = r.headers.get('Range');
  const m = range && /bytes=0-(\d+)/.exec(range);
  return m ? Number(m[1]) + 1 : 0;
}

/** PUTs file[start, end). 308 → more to send; 200/201 → GCS has the whole object. */
export async function putChunk(
  sessionUri: string,
  file: File,
  start: number,
  end: number,
  total: number,
  signal?: AbortSignal,
): Promise<'more' | 'done'> {
  const r = await fetch(sessionUri, {
    method: 'PUT',
    body: file.slice(start, end),
    headers: { 'Content-Range': `bytes ${start}-${end - 1}/${total}` },
    signal,
  });
  if (r.status === 308) return 'more';
  if (r.status === 200 || r.status === 201) return 'done';
  throw new UploadHttpError(r.status);
}

export interface UploadOptions {
  sessionUri: string;
  file: File;
  chunkBytes: number;
  onProgress: (bytesDone: number) => void;
  signal?: AbortSignal;
  retries?: number;
}

export const BACKOFF_BASE_MS = 500;
export const BACKOFF_MAX_MS = 60_000;

export function retryable(e: unknown): boolean {
  if (e instanceof UploadHttpError) return e.status >= 500 || e.status === 429 || e.status === 408;
  if (e instanceof DOMException && e.name === 'AbortError') return false;
  return true; // network failure
}

export const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        reject(new DOMException('aborted', 'AbortError'));
      },
      { once: true },
    );
  });

/**
 * Drives the whole file to GCS. After any failure it re-queries the offset, because GCS may
 * have kept part of the failed chunk; the retry budget is per upload, reset by progress.
 */
export async function uploadFile({ sessionUri, file, chunkBytes, onProgress, signal, retries = 8 }: UploadOptions): Promise<void> {
  const total = file.size;
  let attempt = 0;
  for (;;) {
    try {
      let offset = await queryOffset(sessionUri, total, signal);
      if (offset === 'done') {
        onProgress(total);
        return;
      }
      if (total === 0) {
        await putChunk(sessionUri, file, 0, 0, 0, signal);
        onProgress(0);
        return;
      }
      while (offset < total) {
        const end = Math.min(offset + chunkBytes, total);
        const r = await putChunk(sessionUri, file, offset, end, total, signal);
        attempt = 0; // progress resets the budget
        offset = end;
        onProgress(offset);
        if (r === 'done') return;
      }
      return;
    } catch (e) {
      if (!retryable(e) || attempt >= retries) throw e;
      await sleep(Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_MAX_MS), signal);
      attempt++;
    }
  }
}
